import { type Static, type TSchema, Type } from '@sinclair/typebox';
import { ClientEntitiesSelectedOptionsSchema } from './clientEntities';

export const MAX_PERCENTAGE = 100;
export const MIN_SAVINGS_PERCENTAGE = -100;

export const SavingPercentageRangeSchema = Type.Array(
  Type.Number({ minimum: MIN_SAVINGS_PERCENTAGE, maximum: MAX_PERCENTAGE }),
);
export type SavingsPercentageRange = Static<typeof SavingPercentageRangeSchema>;

export const CriteriasSchema = Type.Optional(
  Type.Record(
    Type.String({
      pattern: '^Attributes/.*',
      examples: 'Attributes/g_quantity',
    }),
    Type.Union([Type.Array(Type.String()), Type.Array(Type.Number())]),
  ),
);
export type Criterias = Static<typeof CriteriasSchema>;

export const CatalogInHeadersSchema = Type.Object({
  catalog: Type.String(),
  currency: Type.String(),
  language: Type.String(),
  organization: Type.String(),
  taxonomy: Type.String(),
});
export type CatalogInHeaders = Static<typeof CatalogInHeadersSchema>;

export const FilterSchema = Type.Object({
  category: Type.Optional(Type.String()),
  dateRange: Type.Optional(Type.Array(Type.Number())),
  criterias: CriteriasSchema,
  suppliers: Type.Optional(Type.Array(Type.String())),
  clientEntities: Type.Optional(ClientEntitiesSelectedOptionsSchema),
});
export type FilterDTO = Static<typeof FilterSchema>;

export const SwapTypeAcceptanceSchema = Type.Union([
  Type.Literal('SIMPLE'),
  Type.Literal('LIKE_FOR_LIKE'),
  Type.Literal('ALL_FEASIBLE_DISCARD_COUNTRY'),
  Type.Literal('ALL_FEASIBLE'),
  Type.Literal('LIKE_FOR_LIKE_SUPPLIER'),
  Type.Literal('ALL_FEASIBLE_SUPPLIER'),
]);
export type SwapTypeAcceptance = Static<typeof SwapTypeAcceptanceSchema>;

export const SwapFiltersSortBySchema = Type.Union([
  Type.Literal('SAVINGS'),
  Type.Literal('SAVINGS_PERCENTAGE'),
  Type.Literal('SIMILARITY'),
  Type.Literal('OPTION_NUMBER'),
]);
export type SwapFiltersSortBy = Static<typeof SwapFiltersSortBySchema>;

export const SwapFiltersSchema = Type.Object({
  catalog: Type.Optional(Type.String()),
  sameSupplier: Type.Boolean(),
  similarityThreshold: Type.Number({ minimum: 0, maximum: 100 }),
  sortBy: SwapFiltersSortBySchema,
  swapTypeAcceptance: SwapTypeAcceptanceSchema,
  savingsPercentageRange: Type.Optional(SavingPercentageRangeSchema),
  unit: Type.String(),
});
export type SwapFilters = Static<typeof SwapFiltersSchema>;

export const PaginatedSchema = <T extends TSchema>(T: T) =>
  Type.Object({
    result: Type.Array(T),
    offset: Type.Number(),
    limit: Type.Number(),
    total: Type.Number(),
  });
export type Paginated<T> = {
  result: T[];
  offset: number;
  limit: number;
  total: number;
};

export const NullableSchema = <T extends TSchema>(T: T) => Type.Union([T, Type.Null()]);

export type MakeRequired<T, K extends keyof T> = Required<Pick<T, K>> & Omit<T, K>;
